import React, { useState } from 'react';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { getDateAvailabilityStatus } from '../data/carsData';

const AvailabilityCalendar = ({ carId, selectedDate, onDateSelect, minDate, bookings = null, blocks = null }) => {
  const [currentMonth, setCurrentMonth] = useState(() => {
    const today = new Date();
    today.setDate(today.getDate() + 1); // Start from tomorrow
    return new Date(today.getFullYear(), today.getMonth(), 1);
  });

  const getMinDateObj = () => {
    const today = new Date();
    today.setDate(today.getDate() + 1);
    return today;
  };

  const formatDate = (date) => {
    return date.toISOString().split('T')[0];
  };

  const isDateInPast = (date) => {
    const minDateObj = getMinDateObj();
    // Compare dates without time
    const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const minDateOnly = new Date(minDateObj.getFullYear(), minDateObj.getMonth(), minDateObj.getDate());
    return dateOnly < minDateOnly;
  };

  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const navigateMonth = (direction) => {
    setCurrentMonth(prev => {
      const newDate = new Date(prev);
      if (direction === 'prev') {
        newDate.setMonth(prev.getMonth() - 1);
        // Prevent navigating to past months
        const minDateObj = getMinDateObj();
        const minMonth = new Date(minDateObj.getFullYear(), minDateObj.getMonth(), 1);
        if (newDate < minMonth) {
          return prev; // Don't change if trying to go to past
        }
      } else {
        newDate.setMonth(prev.getMonth() + 1);
      }
      return newDate;
    });
  };

  const handleDateClick = (day) => {
    const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    if (!isDateInPast(date)) {
      const dateStr = formatDate(date);
      onDateSelect(dateStr);
    }
  };

  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(currentMonth);
    const firstDay = getFirstDayOfMonth(currentMonth);
    const days = [];
    const minDateObj = getMinDateObj();

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="h-10"></div>);
    }

    // Add cells for each day of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
      const dateStr = formatDate(date);
      const isPast = isDateInPast(date);
      const isSelected = selectedDate === dateStr;
      const availabilityStatus = !isPast ? getDateAvailabilityStatus(carId, dateStr, bookings, blocks) : 'unavailable';

      let statusClass = '';
      let statusDot = '';
      
      if (isPast) {
        statusClass = 'opacity-40 cursor-not-allowed bg-gray-100';
      } else {
        switch (availabilityStatus) {
          case 'available':
            statusClass = 'bg-green-50 hover:bg-green-100 border-green-200 text-green-900 cursor-pointer';
            statusDot = 'bg-green-500';
            break;
          case 'limited':
            statusClass = 'bg-yellow-50 hover:bg-yellow-100 border-yellow-200 text-yellow-900 cursor-pointer';
            statusDot = 'bg-yellow-500';
            break;
          case 'unavailable':
            statusClass = 'bg-red-50 hover:bg-red-100 border-red-200 text-red-900 cursor-pointer';
            statusDot = 'bg-red-500';
            break;
          default:
            statusClass = 'bg-gray-50 hover:bg-gray-100 border-gray-200 cursor-pointer';
        }
      }

      if (isSelected) {
        statusClass += ' ring-2 ring-primary-500 ring-offset-2';
      }

      days.push(
        <div
          key={day}
          onClick={() => !isPast && handleDateClick(day)}
          className={`h-10 sm:h-12 flex flex-col items-center justify-center border-2 rounded-xl transition-all ${
            isPast ? statusClass : `${statusClass} hover:scale-110 hover:shadow-md active:scale-95`
          }`}
        >
          <span className="text-sm sm:text-base font-semibold">{day}</span>
          {!isPast && (
            <span className={`w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-full mt-1 shadow-sm ${statusDot}`}></span>
          )}
        </div>
      );
    }

    return days;
  };

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const minDateObj = getMinDateObj();
  const minMonth = new Date(minDateObj.getFullYear(), minDateObj.getMonth(), 1);
  const canGoPrev = currentMonth > minMonth;

  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5 sm:p-7">
      <div className="flex items-center justify-between mb-5 sm:mb-7">
        <button
          onClick={() => navigateMonth('prev')}
          disabled={!canGoPrev}
          className={`p-2 sm:p-2.5 rounded-xl transition-all ${
            canGoPrev 
              ? 'hover:bg-gray-50 hover:shadow-sm cursor-pointer active:scale-95' 
              : 'opacity-30 cursor-not-allowed'
          }`}
        >
          <FiChevronLeft className="w-5 h-5 sm:w-6 sm:h-6 text-gray-700" />
        </button>
        <h3 className="text-lg sm:text-xl font-bold text-gray-900 text-center px-3">
          <span className="hidden sm:inline">{monthNames[currentMonth.getMonth()]} </span>
          <span className="sm:hidden">{monthNames[currentMonth.getMonth()].substring(0, 3)} </span>
          {currentMonth.getFullYear()}
        </h3>
        <button
          onClick={() => navigateMonth('next')}
          className="p-2 sm:p-2.5 hover:bg-gray-50 hover:shadow-sm rounded-xl transition-all active:scale-95"
        >
          <FiChevronRight className="w-5 h-5 sm:w-6 sm:h-6 text-gray-700" />
        </button>
      </div>

      <div className="grid grid-cols-7 gap-2 sm:gap-3 mb-4 sm:mb-5">
        {weekDays.map(day => (
          <div key={day} className="text-center text-xs sm:text-sm font-bold text-gray-500 py-2 sm:py-3 uppercase tracking-wide">
            {day}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-2 sm:gap-3">
        {renderCalendar()}
      </div>

      <div className="mt-6 sm:mt-8 pt-5 sm:pt-6 border-t border-gray-100 flex flex-wrap items-center justify-center gap-4 sm:gap-6 text-xs sm:text-sm">
        <div className="flex items-center gap-2.5">
          <span className="w-3.5 h-3.5 rounded-full bg-green-500 shadow-sm"></span>
          <span className="text-gray-700 font-medium">Available</span>
        </div>
        <div className="flex items-center gap-2.5">
          <span className="w-3.5 h-3.5 rounded-full bg-yellow-500 shadow-sm"></span>
          <span className="text-gray-700 font-medium">Limited</span>
        </div>
        <div className="flex items-center gap-2.5">
          <span className="w-3.5 h-3.5 rounded-full bg-red-500 shadow-sm"></span>
          <span className="text-gray-700 font-medium">Unavailable</span>
        </div>
      </div>
    </div>
  );
};

export default AvailabilityCalendar;

